// src/hooks/useAnalytics.ts
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackEvent } from '../services/analytics';

/**
 * Hook to automatically track page views when route changes
 */
export const usePageTracking = () => {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Don't track if it's the same path (prevents double tracking)
    if (previousPath.current === location.pathname) {
      return;
    }

    previousPath.current = location.pathname;

    // Get page title from document or generate from path
    const getPageTitle = () => {
      // Wait a tick for document.title to update
      setTimeout(() => {
        const title = document.title || getDefaultTitle(location.pathname);
        trackPageView(location.pathname, title);
      }, 100);
    };

    getPageTitle();
  }, [location.pathname]);
};

/**
 * Generate a default title from the path
 */
const getDefaultTitle = (path: string): string => {
  if (path === '/') return 'Home';

  // Remove leading slash and split by /
  const segments = path.slice(1).split('/');

  // Capitalize first segment
  return segments[0]
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Hook to track custom events
 */
export const useEventTracking = () => {
  return {
    trackClick: (elementName: string, additionalData?: Record<string, string | number>) => {
      trackEvent('click', { element: elementName, ...additionalData });
    },
    trackFormSubmit: (formName: string) => {
      trackEvent('form_submit', { form: formName });
    },
    trackDownload: (fileName: string) => {
      trackEvent('download', { file: fileName });
    },
    trackExternalLink: (url: string) => {
      trackEvent('external_link', { url });
    },
    trackCustomEvent: trackEvent,
  };
};

export default usePageTracking;
